import React, { useState, useCallback } from 'react';
import { Box, Button, Alert, Typography, Stack } from '@mui/material';
import {
  AddRounded as AddIcon,
  SettingsRounded as SettingsIcon,
} from '@mui/icons-material';

import { useTwitterConfig, useTwitterAccounts } from '../../../hooks/useTwitterData';
import TwitterConfigDialog from './TwitterConfigDialog';
import TwitterAccountsTable from './TwitterAccountsTable';
import TwitterAccountDialog from './TwitterAccountDialog';
import DeleteConfirmDialog from '../../DeleteConfirmDialog';

export default function TwitterScrapers() {
  const {
    config,
    loading: configLoading,
    saving: configSaving,
    error: configError,
    clearError: clearConfigError,
    saveConfig,
  } = useTwitterConfig();

  const {
    rows,
    loading: rowsLoading,
    error: rowsError,
    setError: setRowsError,
    clearError: clearRowsError,
    createAccount,
    updateAccount,
    deleteAccount,
    toggleActive,
  } = useTwitterAccounts();

  const [configOpen, setConfigOpen] = useState(false);
  const [dialogState, setDialogState] = useState({ open: false, mode: 'add', initialValues: null });
  const [deleteState, setDeleteState] = useState({ open: false, target: null, saving: false });

  const buttonSx = { textTransform: 'none', fontWeight: 600, borderRadius: 1.5 };

  const openAdd = useCallback(() => {
    setDialogState({ open: true, mode: 'add', initialValues: null });
  }, []);

  const openEdit = useCallback((row) => {
    setDialogState({ open: true, mode: 'edit', initialValues: row });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState((s) => ({ ...s, open: false }));
  }, []);

  const handleSubmitAccount = useCallback(async (payload, ctx) => {
    if (ctx.mode === 'edit') {
      const { username, ...patch } = payload;
      await updateAccount(ctx.originalUsername, patch);
    } else {
      await createAccount(payload);
    }
  }, [createAccount, updateAccount]);

  const handleToggleActive = useCallback(async (row, next) => {
    try {
      await toggleActive(row.username, next);
    } catch (err) {
      setRowsError(err?.message || String(err));
    }
  }, [toggleActive, setRowsError]);

  const requestDelete = useCallback((row) => {
    setDeleteState({ open: true, target: row, saving: false });
  }, []);

  const cancelDelete = useCallback(() => {
    setDeleteState({ open: false, target: null, saving: false });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteState.target) return;
    setDeleteState((s) => ({ ...s, saving: true }));
    try {
      await deleteAccount(deleteState.target.username);
      setDeleteState({ open: false, target: null, saving: false });
    } catch (err) {
      setRowsError(err?.message || String(err));
      setDeleteState((s) => ({ ...s, saving: false }));
    }
  }, [deleteState.target, deleteAccount, setRowsError]);

  const intervalCopy = config?.interval_minutes
    ? `Account changes apply on the next Twitter tick (~${config.interval_minutes} min).`
    : 'Account changes apply on the next Twitter tick.';

  return (
    <Box>
      {configError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearConfigError}>
          {configError}
        </Alert>
      )}

      <Stack
        direction="row"
        sx={{ mb: 1.5, alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Accounts
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {intervalCopy}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => setConfigOpen(true)}
            sx={buttonSx}
          >
            Edit Settings
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            disableElevation
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={buttonSx}
          >
            Add Account
          </Button>
        </Stack>
      </Stack>

      {rowsError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={clearRowsError}>
          {rowsError}
        </Alert>
      )}

      <TwitterAccountsTable
        rows={rows}
        loading={rowsLoading}
        onEdit={openEdit}
        onDelete={requestDelete}
        onToggleActive={handleToggleActive}
      />

      <TwitterConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        config={config}
        loading={configLoading}
        saving={configSaving}
        onSave={saveConfig}
      />

      <TwitterAccountDialog
        open={dialogState.open}
        mode={dialogState.mode}
        initialValues={dialogState.initialValues}
        onClose={closeDialog}
        onSubmit={handleSubmitAccount}
      />

      <DeleteConfirmDialog
        open={deleteState.open}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        itemType="Twitter account"
        itemLabel={deleteState.target ? `@${deleteState.target.username}` : ''}
        saving={deleteState.saving}
      />
    </Box>
  );
}
